/**
 * AutoMom skill-checkpoint card.
 *
 * Shown on the Today page when a skill's learning tasks for a level complete.
 * Rather than advancing the level silently, AutoMom asks the user to confirm:
 *  ✓ Yes, I've got this  – advance current_level to the next rung
 *  ↻ Need more practice  – keep the level; dismiss
 *  ? Gap's too big       – route to a first lesson + ask for help
 */
import { useEffect, useState } from 'react'
import { CheckCircle2, GraduationCap, HelpCircle, RotateCcw } from 'lucide-react'
import { Button } from '../ui/Button'
import type {
  SkillCheckpointPrompt,
  SkillCheckpointRespondResult,
  SkillCheckpointResponseCode,
} from '../../services/skillCheckpointService'
import { AskEyebrow } from '../today/AskEyebrow'
import { levelWord } from '../../utils/skillSections'
import { ORI_KIND_LABEL } from '../../utils/oriAsks'

interface Props {
  prompt: SkillCheckpointPrompt
  markShown: (id: string) => void
  respond: (id: string, code: SkillCheckpointResponseCode) => Promise<SkillCheckpointRespondResult>
  onDone: (result: SkillCheckpointRespondResult) => void
}

const LADDER = ['novice', 'beginner', 'developing', 'proficient', 'advanced']
const CAPABILITY: Record<string, string> = {
  novice: 'Not yet able to perform',
  beginner: 'Can follow an example',
  developing: 'Can perform with guidance',
  proficient: 'Can perform independently in normal cases',
  advanced: 'Can handle complex or unfamiliar cases',
}

export function SkillCheckpointCard({ prompt, markShown, respond, onDone }: Props) {
  const [working, setWorking] = useState(false)
  const [done, setDone] = useState(false)

  useEffect(() => {
    markShown(prompt.id)
  }, [prompt.id, markShown])

  if (done) return null

  const skillLabel = prompt.skill_title || 'this skill'
  const target = prompt.unlocks_level || ''
  const targetCapability = CAPABILITY[target] || ''
  const targetIdx = LADDER.indexOf(target)

  const handle = async (code: SkillCheckpointResponseCode) => {
    setWorking(true)
    try {
      const result = await respond(prompt.id, code)
      // M6: keep the card if the backend rejected the response (e.g. already
      // answered, 503, server error) so the user can retry.
      if (!result.success) {
        setWorking(false)
        return
      }
      setDone(true)
      onDone(result)
    } catch {
      setWorking(false)
    }
  }

  return (
    <div className="p-3 rounded-xl border border-brand/30 bg-brand-subtle/40">
      {/* Header */}
      <AskEyebrow icon={<GraduationCap />} label={ORI_KIND_LABEL.decide} typeWord="Skill" className="mb-2" />
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-primary leading-snug">
            Checkpoint: are you at “{levelWord(target) || target}” with {skillLabel}?
          </p>
          {targetCapability && (
            <p className="mt-0.5 text-xs text-secondary">
              That means: {targetCapability.toLowerCase()}.
            </p>
          )}
        </div>
      </div>

      {/* Ladder */}
      {targetIdx >= 0 && (
        <div className="mt-2 flex items-center gap-1">
          {LADDER.map((lvl, i) => (
            <div
              key={lvl}
              title={CAPABILITY[lvl]}
              className={`h-1.5 flex-1 rounded-full transition-all ${
                i <= targetIdx ? 'bg-brand' : 'bg-surface-alt'
              }`}
            />
          ))}
        </div>
      )}
      {targetIdx >= 0 && (
        <div className="mt-1 flex justify-between text-app-micro text-muted">
          {/* The ladder's words, capitalised as on every skill surface (rule 2). */}
          <span>{levelWord(prompt.current_level) || 'Not yet'}</span>
          <span className="text-brand font-medium">{levelWord(target) || target}</span>
        </div>
      )}

      {/* Actions */}
      <div className="mt-3 flex flex-wrap gap-2">
        {/* A Needs-you card's answers are outlines (North Star: /today keeps one
            filled button, "Start this"); the green filled "Yes" was a second one.
            "Gap's too big" read as a verdict; "Not close yet" says the same. */}
        <Button size="xs" variant="outline" disabled={working} onClick={() => void handle('confirm')} className="gap-1 hover:bg-ok/10">
          <CheckCircle2 className="w-3.5 h-3.5" />
          Yes, I've got this
        </Button>
        <Button size="xs" variant="outline" disabled={working} onClick={() => void handle('need_more')} className="gap-1 hover:bg-brand-subtle">
          <RotateCcw className="w-3.5 h-3.5" />
          Need more practice
        </Button>
        <Button size="xs" variant="outline" disabled={working} onClick={() => void handle('ask_for_help')} className="ml-auto gap-1 hover:bg-brand-subtle">
          <HelpCircle className="w-3.5 h-3.5" />
          Not close yet
        </Button>
      </div>
    </div>
  )
}
